import { useCallback, useMemo, useState } from 'react'
import { ListChecks, Plus } from 'lucide-react'
import { ChecklistCard } from './ChecklistCard'
import type { ChecklistSectionProps } from '../type'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible'
import { CollapsibleSectionHeader } from '@/components/app/layout/collapsible-section-header'
import {
  useChecklistMutations,
  useChecklists,
} from '@/lib/db/rxdb/hooks'
import {
  generateInitialRank,
  generateRankBetween,
} from '@/lib/utils/lexorank'
import { logger } from '@/lib/monitoring/logger'

export function ChecklistSection({
  taskId,
  mode = 'list',
  onMakeSubTask,
}: ChecklistSectionProps) {
  const { checklists: rawLists, isLoading } = useChecklists({ taskId })
  const lists = useMemo(
    () =>
      [...rawLists].sort((a, b) =>
        (a.sortOrder ?? '').localeCompare(b.sortOrder ?? ''),
      ),
    [rawLists],
  )
  const { createChecklist } = useChecklistMutations()
  const [isOpen, setIsOpen] = useState(true)

  const handleAddChecklist = useCallback(async () => {
    if (!taskId) return

    // Generate sortOrder after the last list
    const lastSortOrder =
      lists.length > 0 ? (lists[lists.length - 1]?.sortOrder ?? null) : null
    const sortOrder =
      lists.length === 0
        ? generateInitialRank()
        : generateRankBetween(lastSortOrder, null)

    try {
      await createChecklist({
        taskId,
        title: 'Checklist',
        sortOrder,
      })
    } catch (error) {
      logger.logError(error as Error, 'DATABASE', {
        component: 'ChecklistSection',
        taskId,
        operation: 'create_checklist',
      })
    }
  }, [taskId, lists, createChecklist])

  if (isLoading) {
    return null
  }

  if (!taskId) {
    return null
  }

  const hasChecklists = lists.length > 0

  if (mode === 'chip' && !hasChecklists) {
    return (
      <button type="button" onClick={() => void handleAddChecklist()} className='inline-flex h-7 items-center gap-1.5 rounded-md bg-secondary px-2.5 text-[12.5px] text-muted-foreground transition-colors hover:text-foreground'>
        <ListChecks className="size-3.5" />
        Checklist
      </button>
    )
  }

  // Button mode - always show the add button
  if (mode === 'button') {
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => void handleAddChecklist()}
        className="-ml-2 gap-1.5 text-muted-foreground hover:text-accent-foreground"
      >
        <ListChecks className="size-4" />
        Add checklist
      </Button>
    )
  }

  // List mode - show checklists if they exist, nothing if empty
  if (mode === 'list') {
    if (!hasChecklists) {
      return null
    }

    return (
      <div className="space-y-2">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleSectionHeader
            icon={<ListChecks className="size-4" />}
            title="Checklists"
            isOpen={isOpen}
            rightSlot={
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => void handleAddChecklist()}
                className="size-6 shrink-0 text-muted-foreground hover:text-foreground hover:bg-accent"
              >
                <Plus className="size-4" />
              </Button>
            }
          />

          <CollapsibleContent className="space-y-4 pt-2">
            {lists.map((list) => (
              <ChecklistCard key={list.id} list={list} onMakeSubTask={onMakeSubTask} />
            ))}
          </CollapsibleContent>
        </Collapsible>
      </div>
    )
  }

  // Full mode - show button when empty, list with header when not empty
  if (!hasChecklists) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => void handleAddChecklist()}
        className="-ml-2 gap-1.5 text-muted-foreground hover:text-accent-foreground"
      >
        <ListChecks className="size-4" />
        Add checklist
      </Button>
    )
  }

  // One checklist: its header is the section label (design 28a). Several:
  // a "Checklists" label with each list's own title, count and bar under it.
  if (lists.length === 1 && lists[0]) {
    return <ChecklistCard list={lists[0]} flat onMakeSubTask={onMakeSubTask} onAddChecklist={() => void handleAddChecklist()} />
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Checklists</span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Add checklist"
          onClick={() => void handleAddChecklist()}
          className="ml-auto size-6 shrink-0 text-muted-foreground hover:text-foreground"
        >
          <Plus className="size-3.5" />
        </Button>
      </div>
      <div className="space-y-4">
        {lists.map((list) => (
          <ChecklistCard key={list.id} list={list} onMakeSubTask={onMakeSubTask} />
        ))}
      </div>
    </div>
  )
}
